import { useTitle } from '@/hooks/useTitle';
import styles from './styles.module.scss';

export function PrivacyPage() {
  useTitle('DienTEA | Política de Privacidad');

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>Política de Privacidad</h1>
          <p className={styles.subtitle}>Última actualización: mayo de 2026</p>
        </header>

        <section className={styles.section}>
          <div className={styles.notice}>
            <strong>Resumen:</strong> Este sitio web no recoge datos personales de forma activa. No existe formulario de
            registro, ni área privada, ni sistema de comentarios. Los únicos datos que podrían tratarse son los
            derivados del uso de vídeos de YouTube incrustados, gestionados directamente por Google LLC.
          </div>
        </section>

        <section className={styles.section}>
          <h2>1. Responsable del tratamiento</h2>
          <div className={styles.dataBlock}>
            <div className={styles.dataRow}>
              <span className={styles.dataKey}>Titular</span>
              <span className={styles.dataVal}>María Pilar Pecci Lloret</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataKey}>NIF / CIF</span>
              <span className={styles.dataVal}>48618310J</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataKey}>Domicilio</span>
              <span className={styles.dataVal}>
                Av. Marqués de Los Velez, s/n, 30008 Murcia. Hospital Universitario Morales Meseguer. 1 planta. Despacho
                1.25.
              </span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataKey}>Correo electrónico</span>
              <span className={styles.dataVal}>
                <a href='mailto:contacto@dientea.es'>contacto@dientea.es</a>
              </span>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2>2. Datos que se tratan</h2>
          <p>
            Este sitio web <strong>no recaba datos personales identificativos</strong> de sus visitantes. No existen
            formularios de contacto, registro de usuarios, suscripciones ni compras.
          </p>
          <p>
            Sin perjuicio de lo anterior, el proveedor de alojamiento web puede registrar de forma automática datos
            técnicos de acceso (dirección IP, navegador, sistema operativo, páginas visitadas y hora de acceso) con
            fines de seguridad y mantenimiento del servidor. Estos datos no se utilizan para identificar a los usuarios
            ni se ceden a terceros.
          </p>
        </section>

        <section className={styles.section}>
          <h2>3. Datos tratados por terceros: YouTube (Google LLC)</h2>
          <p>
            El sitio incluye vídeos incrustados de YouTube. Cuando se visualiza una página con alguno de estos
            reproductores, Google puede recoger información sobre el dispositivo, navegador, dirección IP y
            comportamiento de reproducción del usuario, de acuerdo con su propia política de privacidad.
          </p>
          <p>
            El responsable de dicho tratamiento es <strong>Google LLC</strong>, con domicilio social en 1600
            Amphitheatre Parkway, Mountain View, California 94043, EE.&nbsp;UU. Para más información sobre cómo Google
            trata sus datos, consulte:
          </p>
          <ul className={styles.list}>
            <li>
              <a href='https://policies.google.com/privacy?hl=es' target='_blank' rel='noopener noreferrer'>
                Política de Privacidad de Google
              </a>
            </li>
            <li>
              <a
                href='https://policies.google.com/technologies/cookies?hl=es'
                target='_blank'
                rel='noopener noreferrer'>
                Tecnologías de cookies de Google
              </a>
            </li>
            <li>
              <a href='https://myaccount.google.com/data-and-privacy' target='_blank' rel='noopener noreferrer'>
                Centro de privacidad de Google (gestionar sus datos)
              </a>
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>4. Finalidad y base jurídica del tratamiento</h2>
          <p>
            Los datos técnicos de acceso registrados por el servidor se tratan con la finalidad de garantizar la
            seguridad y el correcto funcionamiento del sitio web. La base jurídica es el{' '}
            <strong>interés legítimo</strong> del responsable conforme al artículo 6.1.f) del RGPD.
          </p>
          <p>
            El tratamiento de datos derivado de los vídeos de YouTube está amparado por las condiciones de uso de Google
            LLC y, en su caso, por el consentimiento prestado por el usuario en el marco de dichas plataformas.
          </p>
        </section>

        <section className={styles.section}>
          <h2>5. Conservación de los datos</h2>
          <p>
            Los registros técnicos del servidor se conservan durante el tiempo estrictamente necesario para los fines de
            seguridad y, en todo caso, durante los plazos exigidos por la legislación vigente. Una vez cumplida la
            finalidad, los datos se eliminan de forma segura.
          </p>
        </section>

        <section className={styles.section}>
          <h2>6. Cesión de datos a terceros</h2>
          <p>
            No se realizan cesiones de datos a terceros, salvo obligación legal o las inherentes al propio
            funcionamiento del servicio de alojamiento web contratado. Google LLC actúa como responsable independiente
            respecto de los datos que recoge a través de YouTube.
          </p>
        </section>

        <section className={styles.section}>
          <h2>7. Transferencias internacionales</h2>
          <p>
            Google LLC puede transferir datos fuera del Espacio Económico Europeo. Dichas transferencias están amparadas
            por las garantías previstas en el RGPD, incluyendo las Cláusulas Contractuales Tipo adoptadas por la
            Comisión Europea. Para más detalle, consulte la política de privacidad de Google.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Sus derechos</h2>
          <p>
            En relación con los datos técnicos tratados por el responsable de este sitio, puede ejercer los siguientes
            derechos:
          </p>
          <div className={styles.rightsGrid}>
            <div className={styles.rightItem}>
              <strong>Acceso</strong>
              Conocer qué datos suyos se están tratando.
            </div>
            <div className={styles.rightItem}>
              <strong>Rectificación</strong>
              Solicitar la corrección de datos inexactos.
            </div>
            <div className={styles.rightItem}>
              <strong>Supresión</strong>
              Solicitar la eliminación de sus datos cuando proceda.
            </div>
            <div className={styles.rightItem}>
              <strong>Limitación</strong>
              Solicitar la restricción del tratamiento en determinados supuestos.
            </div>
            <div className={styles.rightItem}>
              <strong>Oposición</strong>
              Oponerse al tratamiento basado en interés legítimo.
            </div>
            <div className={styles.rightItem}>
              <strong>Reclamación</strong>
              Presentar una reclamación ante la{' '}
              <a href='https://www.aepd.es' target='_blank' rel='noopener noreferrer'>
                AEPD
              </a>
              .
            </div>
          </div>
          <p style={{ marginTop: '16px' }}>
            Para ejercer sus derechos, puede contactarnos en{' '}
            <a href='mailto:contacto@dientea.es'>contacto@dientea.es</a>. Le responderemos en el plazo máximo de un mes.
          </p>
          <p>
            Para ejercer sus derechos frente a Google respecto de los datos recogidos a través de YouTube, utilice el{' '}
            <a href='https://myaccount.google.com/data-and-privacy' target='_blank' rel='noopener noreferrer'>
              Centro de privacidad de Google
            </a>
            .
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. Menores de edad</h2>
          <p>
            Este sitio web está dirigido a familias con hijos, no a menores de forma directa. No se recaban datos
            personales de menores de 14 años. Si usted tiene constancia de que un menor ha facilitado datos personales a
            través de este sitio, le rogamos que nos lo comunique para proceder a su supresión.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. Cambios en esta política</h2>
          <p>
            Esta Política de Privacidad puede actualizarse para adaptarse a cambios legislativos o del propio servicio.
            La versión vigente estará siempre disponible en esta página con la fecha de la última actualización.
          </p>
        </section>
      </div>
    </main>
  );
}
